class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    // På söksidan (context: page) är fältet ett vanligt formulärfält — ingen live-dropdown.
    this.predictiveDisabled = this.dataset.predictiveDisabled === 'true';
    this.resetButton.addEventListener('click', this.clear.bind(this));
    if (!this.predictiveDisabled) {
      this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
      this.input.addEventListener('focus', this.onFocus.bind(this));
    }
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    if (!this.predictiveDisabled) {
      document.addEventListener('click', this.boundHandleClickOutside);
      this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));
    }

    this.isHeaderSearch = this.closest('header') !== null;
    this.isDrawerSearch = this.closest('.drawer') !== null;

    // Sekvensräknare: skyddar mot att sena svar/räddningspass skriver över nyare sökningar.
    this._searchSeq = 0;

    // Cache section ID at construction time, before drawer moves to document.body
    try {
      this._cachedSectionId = FoxTheme.utils.getSectionId(this);
    } catch (e) {
      this._cachedSectionId = null;
    }

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open');
    });

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  sectionId() {
    return this._cachedSectionId || FoxTheme.utils.getSectionId(this);
  }

  getListbox() {
    return document.getElementById('PredictiveSearchResults-' + this.sectionId());
  }

  onFocus(event) {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );
    this.loadDictionary();
    if (this.isDrawerSearch) {
      this.searchContent.classList.remove('hidden');
      this.classList.add('predictive-search-open');
      if (this.getQuery().length > 0) {
        const url = this.setupURL().toString();
        this.renderSection(url, { seq: ++this._searchSeq, query: this.getQuery(), allowRescue: true });
      }
      return;
    }
    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add('search-open');
      this.toggleNavButtonVisibility(false);
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent.classList.remove('hidden');
    }
    this.classList.add('predictive-search-open');
    if (this.getQuery().length === 0) {
      if (this.searchRecommendationEmpty) {
        this.searchContent.classList.add('hidden');
      }
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url, { seq: ++this._searchSeq, query: this.getQuery(), allowRescue: true });
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this._searchSeq++; // gör pågående fetch/räddningspass inaktuella
    this.removeAttribute('results');
    const listbox = this.getListbox();
    if (listbox) listbox.innerHTML = '';
    this.toggleSearchState(false);
  }

  handleProductTypeChange(evt) {
    const query = this.getQuery();
    if (query.length > 0) {
      const url = this.setupURL().toString();
      this.renderSection(url, { seq: ++this._searchSeq, query: query, allowRescue: true });
    }
  }

  setupURL(termOverride) {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    let search_term = termOverride != null ? termOverride : this.getQuery();
    if (this.searchProductTypes && this.searchProductTypes.value != '') {
      search_term = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(search_term)}`;
    }
    return (
      url.searchParams.set('q', search_term),
      url.searchParams.set('resources[type]', 'query,product,collection,page,article'),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('section_id', this.sectionId()),
      url
    );
  }

  onChange() {
    const query = this.getQuery();
    this._searchSeq++;
    if (query.length === 0) {
      this.clear();
      return;
    }
    this.loadDictionary();
    const url = this.setupURL().toString();
    this.renderSection(url, { seq: this._searchSeq, query: query, allowRescue: true });
  }

  renderSection(url, meta = {}) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url, meta) : this.renderSectionFromFetch(url, meta);
  }

  renderSectionFromCache(url, meta = {}) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText);
    this.setAttribute('results', '');
    this.maybeRescue(meta);
  }

  renderSectionFromFetch(url, meta = {}) {
    this.setLoadingState(true);

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        this.cachedMap.set(url, responseText);
        if (meta.seq && meta.seq !== this._searchSeq) return; // inaktuell sökning
        this.renderSearchResults(responseText);
        this.setAttribute('results', '');
        this.maybeRescue(meta);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => this.setLoadingState(false));
  }

  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + this.sectionId();
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;

        if (this.isHeaderSearch && !this.isDrawerSearch && !document.body.classList.contains('search-open')) {
          document.body.classList.add('search-open');
          this.toggleNavButtonVisibility(true);
          this.classList.add('predictive-search-open');
        } else {
          this.classList.add('predictive-search-open');
        }
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  /* === Förlåtande sök: räddningspass vid 0 träffar ============================
     1) Särskrivning: "lingon kapslar" -> "lingonkapslar"
     2) Stavfel: rätta mot en liten ordlista byggd från butikens produkt- och
        kollektionstitlar (/products.json + /collections.json, cachas i sessionStorage).
     Körs bara när ordinarie sökning gav tomt resultat; visar en "Visar resultat för"-notis. */

  maybeRescue(meta = {}) {
    if (!meta.allowRescue || !meta.query) return;
    const listbox = this.getListbox();
    if (!listbox) return;
    if (!listbox.querySelector('.predictive-search-empty')) return; // det finns träffar — inget att rädda

    this.loadDictionary().then(() => {
      if (meta.seq !== this._searchSeq) return;
      const variants = this.buildRescueVariants(meta.query);
      if (variants.length) this.tryRescueVariants(variants, meta, 0);
    });
  }

  buildRescueVariants(query) {
    const variants = [];
    const q = query.trim();

    // 1) Prefix-expansion: predictive prefix-matchar bara SISTA ordet, så
    //    "lingon serum" ger 0 trots att "lingonberry serum" finns. Expandera
    //    icke-sista ord som är prefix av ett känt ord ur ordlistan.
    const tokens = q.toLowerCase().split(/\s+/);
    if (tokens.length > 1 && this._dict && this._dict.length) {
      let changed = false;
      const expanded = tokens.map((token, index) => {
        if (index === tokens.length - 1) return token; // sista ordet prefix-matchas av Shopify
        if (token.length < 3 || this._dict.includes(token)) return token;
        const hits = this._dict.filter((word) => word.startsWith(token));
        if (hits.length) {
          changed = true;
          return hits.sort((a, b) => a.length - b.length)[0]; // kortaste = närmast användarens ord
        }
        return token;
      });
      if (changed) variants.push(expanded.join(' '));
    }

    // 2) Särskrivning -> ihopskrivet ("lingon kapslar" -> "lingonkapslar")
    if (/\s/.test(q)) {
      variants.push(q.replace(/\s+/g, ''));
    }

    // 3) Stavfelskorrigering mot ordlistan
    const corrected = this.correctQuery(q);
    if (corrected) variants.push(corrected);

    const lowered = q.toLowerCase();
    return [...new Set(variants)].filter((v) => v && v !== q && v !== lowered);
  }

  tryRescueVariants(variants, meta, index) {
    if (index >= variants.length) return;
    if (meta.seq !== this._searchSeq) return;
    const variant = variants[index];
    const url = this.setupURL(variant).toString();

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        if (meta.seq !== this._searchSeq) return;
        const id = 'PredictiveSearchResults-' + this.sectionId();
        const parsedDoc = new DOMParser().parseFromString(responseText, 'text/html');
        const contentElement = parsedDoc.getElementById(id);
        const hasResults = contentElement && contentElement.querySelector('.predictive-search-result');
        if (hasResults) {
          // Tyst räddning: visa variantens resultat utan notis (notisen upplevdes som brus).
          this.cachedMap.set(url, responseText);
          this.renderSearchResults(responseText);
          this.setAttribute('results', '');
        } else {
          this.tryRescueVariants(variants, meta, index + 1);
        }
      })
      .catch(() => this.tryRescueVariants(variants, meta, index + 1));
  }

  showCorrectionNotice(term) {
    const listbox = this.getListbox();
    if (!listbox) return;
    const template = this.dataset.textCorrected || 'Visar resultat för "__TERMS__"';
    // Avkoda HTML-entiteter (&quot; osv) — översättningen kan komma dubbel-escapad ur Liquid.
    const decoded = new DOMParser().parseFromString(template, 'text/html').documentElement.textContent || template;
    const notice = document.createElement('div');
    notice.className = 'search__correction';
    notice.textContent = decoded.replace('__TERMS__', term);
    listbox.prepend(notice);
  }

  /* --- Ordlista + rättning ------------------------------------------------ */

  loadDictionary() {
    if (this._dictPromise) return this._dictPromise;
    const KEY = 'qfs-search-dict-v1';
    try {
      const cached = sessionStorage.getItem(KEY);
      if (cached) {
        this._dict = JSON.parse(cached);
        this._dictPromise = Promise.resolve(this._dict);
        return this._dictPromise;
      }
    } catch (e) {
      /* sessionStorage kan vara blockerad — kör vidare utan cache */
    }
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const safeJson = (url, fallback) =>
      fetch(url)
        .then((r) => (r.ok ? r.json() : fallback))
        .catch(() => fallback);

    this._dictPromise = Promise.all([
      safeJson(root + 'products.json?limit=250', { products: [] }),
      safeJson(root + 'collections.json?limit=250', { collections: [] }),
    ]).then(([productsData, collectionsData]) => {
      const words = new Set();
      const addWords = (title) => {
        String(title || '')
          .toLowerCase()
          .split(/[^\p{L}\p{N}]+/u)
          .forEach((word) => {
            if (word.length >= 4) words.add(word);
          });
      };
      (productsData.products || []).forEach((p) => addWords(p.title));
      (collectionsData.collections || []).forEach((c) => addWords(c.title));
      this._dict = Array.from(words);
      try {
        sessionStorage.setItem(KEY, JSON.stringify(this._dict));
      } catch (e) {
        /* ignorera */
      }
      return this._dict;
    });
    return this._dictPromise;
  }

  correctQuery(query) {
    if (!this._dict || !this._dict.length) return null;
    const tokens = query.toLowerCase().trim().split(/\s+/);
    let changed = false;
    const corrected = tokens.map((token) => {
      if (token.length < 4) return token;
      if (this._dict.includes(token)) return token;
      // Om ordet är ett prefix till ett känt ord skriver användaren troligen fortfarande — rätta inte.
      if (this._dict.some((word) => word.startsWith(token))) return token;
      const maxDistance = token.length >= 8 ? 2 : 1;
      let best = null;
      let bestDistance = maxDistance + 1;
      for (const word of this._dict) {
        if (Math.abs(word.length - token.length) > maxDistance) continue;
        const distance = this.editDistance(token, word, bestDistance);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = word;
          if (bestDistance === 1) break; // bättre än så blir det inte
        }
      }
      if (best && bestDistance <= maxDistance) {
        changed = true;
        return best;
      }
      return token;
    });
    return changed ? corrected.join(' ') : null;
  }

  editDistance(a, b, cap) {
    // Levenshtein med tidig exit när minsta möjliga distans överstiger cap.
    const m = a.length;
    const n = b.length;
    let previous = new Array(n + 1);
    let current = new Array(n + 1);
    for (let j = 0; j <= n; j++) previous[j] = j;
    for (let i = 1; i <= m; i++) {
      current[0] = i;
      let rowMin = current[0];
      for (let j = 1; j <= n; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        current[j] = Math.min(previous[j] + 1, current[j - 1] + 1, previous[j - 1] + cost);
        if (current[j] < rowMin) rowMin = current[j];
      }
      if (rowMin > cap) return cap + 1;
      [previous, current] = [current, previous];
    }
    return previous[n];
  }

  /* ------------------------------------------------------------------------ */

  handleClickOutside(event) {
    if (this.isDrawerSearch) return;
    const target = event.target;
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) &&
        ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
          target.classList.contains('header__search-close'))
      : !this.contains(target);

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);
    if (this.isHeaderSearch && !this.isDrawerSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      this.toggleNavButtonVisibility(!isOpen);
    }
    if (!isOpen && this.searchRecommendationEmpty && !this.isDrawerSearch) {
      this.searchContent?.classList.add('hidden');
    }
  }

  toggleNavButtonVisibility(isOpen) {
    this.toggleNavigationButton && this.toggleNavigationButton.classList.toggle('is-show', isOpen);
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }
}
if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
